import { useState, useEffect } from 'react';
import { Users, UserCheck, UserX, Clock, TrendingUp, Activity } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { fetchDashboardStats, fetchUserGrowthData, DashboardStats, UserGrowthData } from '../../lib/statsService';
import { StatCard, Loading } from '../../components/ui';

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [growthData, setGrowthData] = useState<UserGrowthData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, growthDataResult] = await Promise.all([
        fetchDashboardStats(),
        fetchUserGrowthData(),
      ]);
      setStats(statsData);
      setGrowthData(growthDataResult);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading fullScreen message="ダッシュボードを読み込み中..." />;
  }

  if (!stats) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-admin-muted">データの読み込みに失敗しました</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-admin-accent/10 p-3">
          <Activity className="h-6 w-6 text-admin-accent" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-admin-ink">ダッシュボード</h1>
          <p className="text-sm text-admin-muted">システム全体の統計情報</p>
        </div>
      </div>

      {/* KPIカード */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="総ユーザー数"
          value={stats.totalUsers.toLocaleString()}
          icon={Users}
          description={`今月 ${stats.monthRegistrations} 人が新規登録`}
        />
        
        <StatCard
          title="ドナー数"
          value={stats.totalDonors.toLocaleString()}
          icon={UserCheck}
          description={`全体の ${Math.round((stats.totalDonors / stats.totalUsers) * 100)}%`}
        />
        
        <StatCard
          title="ドニー数"
          value={stats.totalDonees.toLocaleString()}
          icon={UserX}
          description={`全体の ${Math.round((stats.totalDonees / stats.totalUsers) * 100)}%`}
        />
        
        <StatCard
          title="審査待ち"
          value={stats.pendingReviews.toLocaleString()}
          icon={Clock}
          description="ドナー審査待ちの件数"
        />
        
        <StatCard
          title="今日の新規登録"
          value={stats.todayRegistrations.toLocaleString()}
          icon={TrendingUp}
          description="本日の新規ユーザー"
        />
        
        <StatCard
          title="今月の新規登録"
          value={stats.monthRegistrations.toLocaleString()}
          icon={TrendingUp}
          description="今月の新規ユーザー"
        />
      </div>

      {/* ユーザー数推移グラフ */}
      <div className="rounded-2xl border border-admin-border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-bold text-admin-ink">ユーザー数推移（過去30日）</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={growthData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="total"
                name="総ユーザー数"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="donors"
                name="ドナー"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="donees"
                name="ドニー"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 統計サマリー */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-admin-border bg-white p-6 shadow-sm">
          <h3 className="mb-4 font-bold text-admin-ink">ユーザー構成</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-admin-muted">ドナー</span>
              <div className="flex items-center gap-2">
                <div className="h-2 w-32 overflow-hidden rounded-full bg-admin-bg">
                  <div
                    className="h-full bg-blue-500"
                    style={{
                      width: `${(stats.totalDonors / stats.totalUsers) * 100}%`,
                    }}
                  />
                </div>
                <span className="text-sm font-semibold text-admin-ink">
                  {stats.totalDonors}人
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-admin-muted">ドニー</span>
              <div className="flex items-center gap-2">
                <div className="h-2 w-32 overflow-hidden rounded-full bg-admin-bg">
                  <div
                    className="h-full bg-green-500"
                    style={{
                      width: `${(stats.totalDonees / stats.totalUsers) * 100}%`,
                    }}
                  />
                </div>
                <span className="text-sm font-semibold text-admin-ink">
                  {stats.totalDonees}人
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-admin-border bg-white p-6 shadow-sm">
          <h3 className="mb-4 font-bold text-admin-ink">クイック統計</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-admin-muted">ドナー比率</span>
              <span className="text-sm font-semibold text-admin-ink">
                {Math.round((stats.totalDonors / stats.totalUsers) * 100)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-admin-muted">ドニー比率</span>
              <span className="text-sm font-semibold text-admin-ink">
                {Math.round((stats.totalDonees / stats.totalUsers) * 100)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-admin-muted">審査待ち率</span>
              <span className="text-sm font-semibold text-admin-ink">
                {stats.totalDonors > 0
                  ? Math.round((stats.pendingReviews / stats.totalDonors) * 100)
                  : 0}
                %
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
